import React, { useState, useEffect } from "react";
import "../assets/stylesheet/layout.scss";
import "../assets/stylesheet/dashboard.scss";
import "font-awesome/css/font-awesome.min.css";
import Button from "react-bootstrap/Button";
import ReactPaginate from "react-paginate";
import { ShowToastUtil } from "../utilities/showToastUtil";
import ConfirmationPopUpUtil from "../utilities/confirmationPopUpUtil";
import ApiRequest from "../services/apiService";
import MyMultiSelectDropdown from "../utilities/multiSelectInputType";
import baseUrl from "../services/baseUrl";
import "../assets/stylesheet/multiSelectInputType.css";
import GetUserData from "../utilities/getUserDetails";

function UserMaster() {
  // State hooks
  const [users, setUsers] = useState([]);
  const [Projects, setProjects] = useState([]);
  const [preSelectedOptions, setPreSelectedOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [sortConfig, setSortConfig] = useState({
    key: "UserName",
    direction: "ascending",
  });
  const [formValues, setFormValues] = useState({
    UserId: "",
    UserName: "",
    UserEmail: "",
    UserPassword: "",
    UserMobile: "",
    UserRole: "",
    Designation: "",
    ProjectId: "",
    WorkLocation: "",
    IsActive: "",
  });
  const [popUpMessage, setPopUpMessage] = useState(null);
  const [isPopUpVisible, setIsPopUpVisible] = useState(false);
  const [popUpAction, setPopUpAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const locations = ["Location X", "Location Y", "Location Z"];

  // Fetch users and projects from API
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null); // Reset error state on new fetch attempt
      try {
        const response = await ApiRequest({
          apiLink: `${baseUrl()}/nps/GetUsers?queryMode=${"fetch"}`,
          method: "GET",
        });
        if (response && response.Data) {
          const users = response.Data.NpsUsers;
          const projects = response.Data.Projects;

          // Create a map for quick lookup of project names by ProjectId
          const projectMap = new Map(
            projects.map((project) => [project.ProjectId, project.ProjectName])
          );

          // Update users with project names based on ProjectIds
          const updatedUsers = users.map((user) => {
            // Split the ProjectIds string into an array and get the corresponding project names
            const projectNames = user.ProjectIds.split(",")
              .map((id) => projectMap.get(parseInt(id, 10)))
              .filter((name) => name);

            return {
              ...user,
              ProjectNames: projectNames, // Add the ProjectNames field to user
            };
          });

          setUsers(updatedUsers);
          setProjects(projects);
        } else {
          ShowToastUtil("No users available", "info");
        }
      } catch (err) {
        setError("Failed to fetch users");
        ShowToastUtil("Failed to fetch users", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Handlers
  const handleAdd = () => {
    resetForm();
    setIsAdding(true);
  };

  const handleUpdateUser = async (user) => {
    try {
      const params = new URLSearchParams({
        userId: user ? user.UserId : "",
        queryMode: "fetch",
        userName: user ? user.UserName : "",
      });
      const url = `${baseUrl()}/nps/GetUsers?${params.toString()}`;
      const response = await ApiRequest({
        apiLink: url,
        method: "GET",
      });
      if ((response.Message = "Success")) {
        const userData = response.Data.NpsUsers[0];
        setSelectedUser(userData);
        setPreSelectedOptions(userData.ProjectIds.split(",").map(Number));
        setFormValues({
          UserId: userData.UserId,
          UserName: userData.UserName,
          UserEmail: userData.UserEmail,
          UserPassword: userData.UserPassword,
          UserMobile: userData.UserMobile,
          UserRole: userData.UserRole.trimEnd(),
          ProjectId: userData.ProjectIds,
          Designation: userData.Designation,
          WorkLocation: userData.WorkLocation,
          IsActive: userData.IsActive === 0 ? "In-active" : "Active",
        });
        setIsAdding(true);
      }
    } catch (error) {
      ShowToastUtil("Failed to fetch user", "error");
    }
  };
  const handleConfirm = () => {
    if (popUpAction) {
      popUpAction();
      setPopUpAction(null);
    }
    setIsPopUpVisible(false);
  };

  const handleCancel = () => {
    setPopUpAction(null);
    setIsPopUpVisible(false);
  };

  const handleInactiveClick = (id) => {
    setPopUpMessage("Are you sure you want to mark this user as In-active?");
    setPopUpAction(() => async () => {
      try {
        const url = `${baseUrl()}/nps/CreateUser?userId=${id}&isActive=${0}&queryMode=${"statusUpdate"}`;
        const response = await ApiRequest({
          apiLink: url,
          method: "PUT",
        });
        if ((response.Message = "Success")) {
          setUsers(
            users.map((user) =>
              user.UserId === id ? { ...user, IsActive: 0 } : user
            )
          );
          ShowToastUtil("User marked as In-active", "info");
        }
      } catch (error) {
        ShowToastUtil("Failed to update user status", "error");
      }
    });
    setIsPopUpVisible(true);
  };

  const handleActiveClick = (id) => {
    setPopUpMessage("Are you sure you want to mark this user as Active?");
    setPopUpAction(() => async () => {
      try {
        const url = `${baseUrl()}/nps/CreateUser?userId=${id}&isActive=${1}&queryMode=${"statusUpdate"}`;
        const response = await ApiRequest({
          apiLink: url,
          method: "PUT",
        });
        if ((response.Message = "Success")) {
          setUsers(
            users.map((user) =>
              user.UserId === id ? { ...user, IsActive: 1 } : user
            )
          );
          ShowToastUtil("User marked as Active", "info");
        }
      } catch (error) {
        ShowToastUtil("Failed to update user status", "error");
      }
    });
    setIsPopUpVisible(true);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "ascending"
        ? "descending"
        : "ascending";
    setSortConfig({ key, direction });
  };

  const handlePageSizeChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(0);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      const {
        UserId,
        UserName,
        UserEmail,
        UserPassword,
        UserMobile,
        UserRole,
        ProjectId,
        Designation,
        WorkLocation,
        IsActive,
      } = formValues;
      const params = new URLSearchParams({
        userId: selectedUser ? UserId : "",
        userName: UserName.trim(),
        userEmail: UserEmail.trim(),
        userPassword: UserPassword.trim(),
        userMobile: UserMobile.trim(),
        userRole: UserRole.trim(),
        isActive: IsActive === "Active" ? 1 : 0,
        workLocation: WorkLocation.trim(),
        projectIds: ProjectId || "",
        designation: Designation || "",
        queryMode: selectedUser ? "updation" : "creation",
      });

      const url = `${baseUrl()}/nps/CreateUser?${params.toString()}`;

      const response = await ApiRequest({
        apiLink: url,
        method: "PUT",
      });
      if (selectedUser) {
        setUsers(
          users.map((user) => (user.UserId === UserId ? response : user))
        );
        if (response.Message === "Success") {
          ShowToastUtil("User updated", "success");
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
        else{
          ShowToastUtil(response.Message, "error");
        }
      } else {
        setUsers([...users, response]);
        if (response.Message === "Success") {
          ShowToastUtil("User added", "success");
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
        else{
          ShowToastUtil(response.Message, "error");
        }
      }
      setIsAdding(false);
      resetForm();
    } catch (error) {
      ShowToastUtil(error+' : '+"Failed to save user", "error");
    }
  };

  const resetForm = () => {
    setFormValues({
      UserId: "",
      UserName: "",
      UserEmail: "",
      UserPassword: "",
      UserMobile: "",
      UserRole: "",
      ProjectId: "",
      Designation: "",
      WorkLocation: "",
      IsActive: "",
    });
    setSelectedUser(null);
    setPreSelectedOptions([]);
  };

  const sortedData = [...users].sort((a, b) => {
    if (
      a &&
      b &&
      a[sortConfig.key] !== undefined &&
      b[sortConfig.key] !== undefined
    ) {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
    }
    return 0;
  });

  const filteredData = sortedData.filter(
    (item) =>
      item &&
      Object.values(item).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
  );

  const paginatedData = filteredData.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const roles = ["Admin", "Lab", "Sales"];
  const statuses = ["Active", "In-active"];

  const userRole = GetUserData().UserRole;
  useEffect(() => {
    if (userRole != "Admin") {
      return <div>Restricted for this user</div>;
    }
  }, []);

  return (
    <div className="row">
      {loading ? (
        <div className="col-12 text-center">
          <p>Loading...</p>
        </div>
      ) : error ? (
        <div className="col-12 text-center">
          <p>{error}</p>
        </div>
      ) : !isAdding ? (
        <>
          <div className="col-12 mb-2">
            <div className="card">
              <div className="card-body pt-2 pb-2">
                <div className="card-title px-0 d-flex align-items-center">
                  <h2>User List</h2>
                  <div className="card-option d-flex ms-auto">
                    <Button variant="primary" onClick={handleAdd}>
                      Add New User
                    </Button>
                  </div>
                </div>
                <div className="card-content">
                  <div className="row align-items-center g-2">
                    <div className="col-12 col-sm-6">
                      <div className="d-flex align-items-center">
                        <label htmlFor="page-size">Items per page:</label>
                        <select
                          className="form-control-sm h-30 me-3"
                          id="page-size"
                          value={itemsPerPage}
                          onChange={handlePageSizeChange}
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={15}>15</option>
                          <option value={20}>20</option>
                        </select>
                        <input
                          className="form-control-sm form-control h-30 w-200px"
                          type="text"
                          value={searchTerm}
                          onChange={handleSearchChange}
                          placeholder="Type here to search ..."
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <p className="mb-1 me-1 paginationTotalCount">
                        Total items: {filteredData.length}
                      </p>
                      <ReactPaginate
                        previousLabel={"Previous"}
                        nextLabel={"Next"}
                        breakLabel={"..."}
                        pageCount={Math.ceil(
                          filteredData.length / itemsPerPage
                        )}
                        marginPagesDisplayed={2}
                        pageRangeDisplayed={5}
                        onPageChange={handlePageClick}
                        containerClassName={"pagination"}
                        activeClassName={"active"}
                      />
                    </div>

                    <div className="col-12">
                      <div className="table-responsive custom-table">
                        <table className="table table-sm table-bordered table-hover">
                          <thead>
                            <tr>
                              {[
                                "UserName",
                                "UserEmail",
                                "UserMobile",
                                "Project",
                                "Designation",
                                "WorkLocation Code",
                                "WorkLocation Name",
                                "UserRole",
                                "IsActive",
                              ].map((key) => (
                                <th key={key} onClick={() => handleSort(key)}>
                                  {key}{" "}
                                  {sortConfig.key === key
                                    ? sortConfig.direction === "ascending"
                                      ? "↑"
                                      : "↓"
                                    : ""}
                                </th>
                              ))}
                              <th className="text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedData.map((item) => {
                              // Check if ProjectIds exists and is a string
                              const projectIds =
                                item.ProjectIds &&
                                typeof item.ProjectIds === "string"
                                  ? item.ProjectIds.split(",").map((id) =>
                                      parseInt(id, 10)
                                    )
                                  : []; // Default to an empty array if ProjectIds is undefined or not a string

                              // Filter projects based on user's ProjectIds
                              const filteredProjects = Projects.filter(
                                (project) =>
                                  projectIds.includes(project.ProjectId)
                              );
                              return (
                                <tr key={item.UserId}>
                                  <td>{item.UserName}</td>
                                  <td>{item.UserEmail}</td>
                                  <td>{item.UserMobile}</td>
                                  <td>
                                    {filteredProjects.map((project, index) => (
                                      <span key={project.ProjectId}>
                                        {project.ProjectName}
                                        {index < filteredProjects.length - 1 &&
                                          ", "}
                                      </span>
                                    ))}
                                  </td>
                                  <td>{item.Designation}</td>
                                  <td>{item.WorkLocation}</td>
                                  <td>{item.WorkLocation}</td>
                                  <td>{item.UserRole}</td>
                                  <td>
                                    {item.IsActive === 0
                                      ? "In-active"
                                      : "Active"}
                                  </td>
                                  <td className="text-center">
                                    <button
                                      className="btn px-2 py-1 me-1 btn-sm btn-primary"
                                      onClick={() => handleUpdateUser(item)}
                                    >
                                      Edit
                                    </button>
                                    {item.IsActive === 0 ? (
                                      <button
                                        className="btn px-2 py-1 btn-sm btn-success"
                                        onClick={() =>
                                          handleActiveClick(item.UserId)
                                        }
                                      >
                                        Active
                                      </button>
                                    ) : (
                                      <button
                                        className="btn px-2 py-1 btn-sm btn-danger"
                                        onClick={() =>
                                          handleInactiveClick(item.UserId)
                                        }
                                      >
                                        In-active
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="col-12 mb-2">
          <div className="card">
            <div className="card-body pt-2 pb-3">
              <div className="card-title px-0 d-flex align-items-center">
                <h2>User Form</h2>
                <div className="card-option d-flex ms-auto">
                  <Button variant="primary" onClick={() => setIsAdding(false)}>
                    Back to User List
                  </Button>
                </div>
              </div>
              <div className="card-content">
                <div className="row align-items-center">
                  {["UserName", "UserEmail", "UserMobile", "Designation"].map(
                    (field, index) => (
                      <div
                        key={index}
                        className="col-12 col-sm-6 col-md-4 col-lg-3"
                      >
                        <div className="did-floating-label-content">
                          <input
                            type={
                              field === "UserEmail"
                                ? "email"
                                : field === "UserMobile"
                                ? "tel"
                                : "text"
                            }
                            name={field}
                            className="did-floating-input"
                            value={formValues[field]}
                            onChange={handleFormChange}
                            placeholder=""
                            maxLength={
                              field === "UserName"
                                ? 20
                                : field === "UserMobile"
                                ? 10
                                : field === "UserEmail"
                                ? 60
                                : 50
                            }
                            inputMode={
                              field === "UserMobile" ? "numeric" : undefined
                            }
                            pattern={
                              field === "UserName"
                                ? "^[a-zA-Z0-9_]*$"
                                : field === "UserMobile"
                                ? "[0-9]*"
                                : field === "UserEmail"
                                ? "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,}$"
                                : undefined
                            }
                            required
                          />
                          <label className="did-floating-label">{field}</label>

                          {/* Error message for UserMobile */}
                          {field === "UserMobile" &&
                            formValues[field] &&
                            !/^\d{10}$/.test(formValues[field]) && (
                              <span className="error-message">
                                Please enter a valid 10-digit mobile number.
                              </span>
                            )}

                          {/* Error message for UserEmail */}
                          {field === "UserEmail" &&
                            formValues[field] &&
                            !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
                              formValues[field]
                            ) && (
                              <span className="error-message">
                                Please enter a valid email address.
                              </span>
                            )}

                          {/* Error message for UserName */}
                          {field === "UserName" &&
                            formValues[field] &&
                            !/^[a-zA-Z0-9_]*$/.test(formValues[field]) && (
                              <span className="error-message">
                                User name can only contain letters, numbers, and
                                underscores.
                              </span>
                            )}
                        </div>
                      </div>
                    )
                  )}
                  <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                    <div className="did-floating-label-content">
                      <select
                        name="UserRole"
                        className="did-floating-select"
                        value={formValues.UserRole}
                        onChange={handleFormChange}
                      >
                        <option value="">Select Role</option>
                        {roles.map((role, index) => (
                          <option key={index} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                      <label className="did-floating-label">Role</label>
                    </div>
                  </div>
                  <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                    <div className="did-floating-label-content">
                      <select
                        name="IsActive"
                        className="did-floating-select"
                        value={formValues.IsActive}
                        onChange={handleFormChange}
                      >
                        <option value="">Select Status</option>
                        {statuses.map((status, index) => (
                          <option key={index} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                      <label className="did-floating-label">Status</label>
                    </div>
                  </div>
                  <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                    <div className="did-floating-label-content">
                      <select
                        name="WorkLocation"
                        className="did-floating-select"
                        value={formValues.WorkLocation}
                        onChange={handleFormChange}
                      >
                        <option value="">Select Location</option>
                        {locations.map((location, index) => (
                          <option key={index} value={location}>
                            {location}
                          </option>
                        ))}
                      </select>
                      <label className="did-floating-label">
                        Work Location
                      </label>
                    </div>
                  </div>
                  <div className="col-12 text-center">
                    <Button
                      variant="primary"
                      className="me-2"
                      onClick={handleSave}
                    >
                      {selectedUser ? "Update" : "Save"}
                    </Button>
                    <Button variant="secondary" onClick={resetForm}>
                      Clear
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmationPopUpUtil
        show={isPopUpVisible}
        message={popUpMessage}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}

export default UserMaster;
